import { useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { Controller, useForm, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { NETSUITE_OUTPUT_MAPPER_KEY } from './constants.ts';
import CheckboxGroup from '../../../core/components/CheckboxGroup.tsx';
import BooleanField from '../../../core/components/Fields/BooleanField.tsx';
import SelectField from '../../../core/components/Fields/SelectField.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import FormField from '../../../core/components/FormField.tsx';
import { GET_NAVIGATION_CHANNELS } from '../../../core/components/Navigation/internal/queries.ts';
import { SecretField } from '../../../core/components/Secret.tsx';
import { OutputMapperEditorPanel } from '../../components/OutputMapper/index.ts';

export const MASTER_DATA_MODE_ORDER_RFQ_UNIVERSAL = 'order_rfq_universal';
export const MASTER_DATA_MODE_INVOICE = 'invoice';

export const FORM_DEFAULT_VALUES = {
    name: '',
    clientId: '',
    clientSecret: '',
    tokenId: '',
    tokenSecret: '',
    accountId: '',
    instanceUrl: '',
    restServiceUrl: '',
    masterDataMode: MASTER_DATA_MODE_ORDER_RFQ_UNIVERSAL,
    orderFileCabinetFolderId: '',
    orderCustomFormId: '',
    orderChannelIds: [] as string[],
    rfqFileCabinetFolderId: '',
    rfqCustomFormId: '',
    rfqChannelIds: [] as string[],
    universalFileCabinetFolderId: '',
    universalChannelIds: [] as string[],
    invoiceFileCabinetFolderId: '',
    invoiceCustomFormId: '',
    invoiceChannelIds: [] as string[],
    defaultApprovalStatus: '',
    defaultExpenseAccountId: '',
    defaultSubsidiaryId: '',
    defaultLocationId: '',
    useAltaviaMapping: false,
    altaviaRestletUrl: '',
};

export type NetsuiteFormData = typeof FORM_DEFAULT_VALUES;

export interface NetsuiteIntegration {
    id: string;
    name: string;
    clientId: string;
    tokenId: string;
    accountId: string;
    instanceUrl: string;
    restServiceUrl: string;
    masterDataMode: string;
    orderFileCabinetFolderId?: string | null;
    orderCustomFormId?: string | null;
    orderChannels: { id: string; name: string }[];
    rfqFileCabinetFolderId?: string | null;
    rfqCustomFormId?: string | null;
    rfqChannels: { id: string; name: string }[];
    universalFileCabinetFolderId?: string | null;
    universalChannels?: { id: string; name: string }[];
    invoiceFileCabinetFolderId?: string | null;
    invoiceCustomFormId?: string | null;
    invoiceChannels?: { id: string; name: string }[];
    defaultApprovalStatus?: string | null;
    defaultExpenseAccountId?: string | null;
    defaultSubsidiaryId?: string | null;
    defaultLocationId?: string | null;
    useAltaviaMapping?: boolean | null;
    altaviaRestletUrl?: string | null;
}

type TabType = 'configuration' | 'invoiceMapper';

const Tab = ({ label, isActive, onClick }: { label: string; isActive: boolean; onClick: () => void }) => (
    <button
        type="button"
        onClick={onClick}
        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            isActive
                ? 'border-accent-primary text-accent-primary'
                : 'border-transparent text-content-weak hover:text-content hover:border-border-strong'
        }`}
    >
        {label}
    </button>
);

export interface NetsuiteIntegrationFormProps {
    integration?: NetsuiteIntegration;
    onSubmit: (data: NetsuiteFormData, onSuccess?: (updated: NetsuiteIntegration) => void) => Promise<void>;
    formId?: string;
}

const NetsuiteIntegrationForm = ({
    integration,
    onSubmit,
    formId = 'netsuite-integration-form',
}: NetsuiteIntegrationFormProps) => {
    const { t } = useTranslation('integrations');

    const form: UseFormReturn<NetsuiteFormData> = useForm<NetsuiteFormData>({
        defaultValues: FORM_DEFAULT_VALUES,
        mode: 'onTouched',
    });
    const formErrors = form.formState.errors;
    const masterDataMode = form.watch('masterDataMode');
    const isInvoiceMode = masterDataMode === MASTER_DATA_MODE_INVOICE;

    const [activeTab, setActiveTab] = useState<TabType>('configuration');

    useEffect(() => {
        if (integration) {
            form.reset({
                ...FORM_DEFAULT_VALUES,
                name: integration.name,
                clientId: integration.clientId,
                tokenId: integration.tokenId,
                accountId: integration.accountId,
                instanceUrl: integration.instanceUrl,
                restServiceUrl: integration.restServiceUrl,
                orderFileCabinetFolderId: integration.orderFileCabinetFolderId || '',
                orderCustomFormId: integration.orderCustomFormId || '',
                orderChannelIds: integration.orderChannels.map((c) => c.id),
                rfqCustomFormId: integration.rfqCustomFormId || '',
                rfqFileCabinetFolderId: integration.rfqFileCabinetFolderId || '',
                rfqChannelIds: integration.rfqChannels.map((c) => c.id),
                universalFileCabinetFolderId: integration.universalFileCabinetFolderId || '',
                universalChannelIds: integration.universalChannels?.map((c) => c.id) || [],
                masterDataMode: integration.masterDataMode || MASTER_DATA_MODE_ORDER_RFQ_UNIVERSAL,
                invoiceFileCabinetFolderId: integration.invoiceFileCabinetFolderId || '',
                invoiceCustomFormId: integration.invoiceCustomFormId || '',
                invoiceChannelIds: integration.invoiceChannels?.map((c) => c.id) || [],
                defaultApprovalStatus: integration.defaultApprovalStatus || '',
                defaultExpenseAccountId: integration.defaultExpenseAccountId || '',
                defaultSubsidiaryId: integration.defaultSubsidiaryId || '',
                defaultLocationId: integration.defaultLocationId || '',
                useAltaviaMapping: integration.useAltaviaMapping ?? false,
                altaviaRestletUrl: integration.altaviaRestletUrl || '',
            });
        }
    }, [integration]);

    const { data: channelData } = useQuery(GET_NAVIGATION_CHANNELS, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: false,
    });
    const orderChannels = channelData?.orderProcessingChannels || [];
    const rfqChannels = channelData?.rfqProcessingChannels || [];
    const universalChannels = channelData?.universalProcessingChannels || [];
    const invoiceChannels = channelData?.invoiceProcessingChannels || [];

    return (
        <div className="max-w-6xl w-full flex flex-col flex-1 min-h-0">
            {isInvoiceMode && (
                <div className="flex border-b border-border mb-4">
                    <Tab
                        label={t('netsuite.form.tabs.configuration', 'Configuration')}
                        isActive={activeTab === 'configuration'}
                        onClick={() => setActiveTab('configuration')}
                    />
                    <Tab
                        label={t('netsuite.form.invoiceOutputMapperConfig', 'Invoice Output Mapper')}
                        isActive={activeTab === 'invoiceMapper'}
                        onClick={() => setActiveTab('invoiceMapper')}
                    />
                </div>
            )}

            {activeTab === 'configuration' && (
                <form
                    className="flex flex-col gap-6 flex-1 min-h-0"
                    onSubmit={form.handleSubmit((data) => onSubmit(data))}
                    id={formId}
                >
                    <Controller
                        name="name"
                        control={form.control}
                        render={({ field }) => (
                            <FormField label={t('netsuite.form.name')} error={formErrors.name}>
                                <StringField {...field} />
                            </FormField>
                        )}
                    />

                    <Controller
                        name="masterDataMode"
                        control={form.control}
                        render={({ field }) => (
                            <FormField
                                label={t('netsuite.form.masterDataMode')}
                                error={formErrors.masterDataMode}
                                helpText={t('netsuite.form.masterDataModeHelpText')}
                            >
                                <SelectField
                                    value={field.value}
                                    onValueChange={(value) => field.onChange({ target: { value } })}
                                    options={[
                                        {
                                            label: t('netsuite.form.masterDataModeOption.orderRfqUniversal'),
                                            value: MASTER_DATA_MODE_ORDER_RFQ_UNIVERSAL,
                                        },
                                        {
                                            label: t('netsuite.form.masterDataModeOption.invoice'),
                                            value: MASTER_DATA_MODE_INVOICE,
                                        },
                                    ]}
                                />
                            </FormField>
                        )}
                    />

                    <div className="text-lg font-semibold mt-2">
                        {t('netsuite.form.header.integrationAuthentication')}
                    </div>

                    <Controller
                        name="clientId"
                        control={form.control}
                        render={({ field }) => (
                            <FormField label={t('netsuite.form.clientId')} error={formErrors.clientId}>
                                <StringField {...field} />
                            </FormField>
                        )}
                    />

                    <Controller
                        name="clientSecret"
                        control={form.control}
                        render={({ field }) => (
                            <FormField
                                label={t('netsuite.form.clientSecret')}
                                error={formErrors.clientSecret}
                                helpText={t('secretUpdateOnly')}
                            >
                                <SecretField
                                    {...field}
                                    secretName="clientSecret"
                                    secretId={integration?.id}
                                    defaultIsVisible
                                />
                            </FormField>
                        )}
                    />

                    <Controller
                        name="tokenId"
                        control={form.control}
                        render={({ field }) => (
                            <FormField label={t('netsuite.form.tokenId')} error={formErrors.tokenId}>
                                <StringField {...field} />
                            </FormField>
                        )}
                    />

                    <Controller
                        name="tokenSecret"
                        control={form.control}
                        render={({ field }) => (
                            <FormField
                                label={t('netsuite.form.tokenSecret')}
                                error={formErrors.tokenSecret}
                                helpText={t('secretUpdateOnly')}
                            >
                                <SecretField
                                    {...field}
                                    secretName="netsuiteTokenSecret"
                                    secretId={integration?.id}
                                    defaultIsVisible
                                />
                            </FormField>
                        )}
                    />

                    <Controller
                        name="accountId"
                        control={form.control}
                        render={({ field }) => (
                            <FormField label={t('netsuite.form.accountId')} error={formErrors.accountId}>
                                <StringField {...field} />
                            </FormField>
                        )}
                    />

                    <Controller
                        name="instanceUrl"
                        control={form.control}
                        render={({ field }) => (
                            <FormField label={t('netsuite.form.instanceUrl')} error={formErrors.instanceUrl}>
                                <StringField {...field} />
                            </FormField>
                        )}
                    />

                    <Controller
                        name="restServiceUrl"
                        control={form.control}
                        render={({ field }) => (
                            <FormField label={t('netsuite.form.restServiceUrl')} error={formErrors.restServiceUrl}>
                                <StringField {...field} />
                            </FormField>
                        )}
                    />

                    {!isInvoiceMode && (
                        <>
                            <div className="text-lg font-semibold mt-2">
                                {t('netsuite.form.header.integrationOrders')}
                            </div>

                            <Controller
                                name="orderFileCabinetFolderId"
                                control={form.control}
                                render={({ field }) => (
                                    <FormField
                                        label={t('netsuite.form.orderFileCabinetFolderId')}
                                        error={formErrors.orderFileCabinetFolderId}
                                        helpText={t('netsuite.form.orderFileCabinetFolderIdHelpText')}
                                    >
                                        <StringField {...field} />
                                    </FormField>
                                )}
                            />

                            <Controller
                                name="orderCustomFormId"
                                control={form.control}
                                render={({ field }) => (
                                    <FormField
                                        label={t('netsuite.form.orderCustomFormId')}
                                        error={formErrors.orderCustomFormId}
                                        helpText={t('netsuite.form.orderCustomFormIdHelpText')}
                                    >
                                        <StringField {...field} />
                                    </FormField>
                                )}
                            />

                            <Controller
                                name="orderChannelIds"
                                control={form.control}
                                render={({ field }) => (
                                    <FormField
                                        label={t('netsuite.form.orderChannelIds')}
                                        error={formErrors.orderChannelIds}
                                    >
                                        <CheckboxGroup
                                            {...field}
                                            options={orderChannels.map((c) => ({ value: c.id, label: c.name }))}
                                            onValueChange={(value) => field.onChange({ target: { value } })}
                                        />
                                    </FormField>
                                )}
                            />

                            <div className="text-lg font-semibold mt-2">
                                {t('netsuite.form.header.integrationRFQs')}
                            </div>

                            <Controller
                                name="rfqFileCabinetFolderId"
                                control={form.control}
                                render={({ field }) => (
                                    <FormField
                                        label={t('netsuite.form.rfqFileCabinetFolderId')}
                                        error={formErrors.rfqFileCabinetFolderId}
                                        helpText={t('netsuite.form.rfqFileCabinetFolderIdHelpText')}
                                    >
                                        <StringField {...field} />
                                    </FormField>
                                )}
                            />

                            <Controller
                                name="rfqCustomFormId"
                                control={form.control}
                                render={({ field }) => (
                                    <FormField
                                        label={t('netsuite.form.rfqCustomFormId')}
                                        error={formErrors.rfqCustomFormId}
                                        helpText={t('netsuite.form.rfqCustomFormIdHelpText')}
                                    >
                                        <StringField {...field} />
                                    </FormField>
                                )}
                            />

                            <Controller
                                name="rfqChannelIds"
                                control={form.control}
                                render={({ field }) => (
                                    <FormField
                                        label={t('netsuite.form.rfqChannelIds')}
                                        error={formErrors.rfqChannelIds}
                                    >
                                        <CheckboxGroup
                                            {...field}
                                            options={rfqChannels.map((c) => ({ value: c.id, label: c.name }))}
                                            onValueChange={(value) => field.onChange({ target: { value } })}
                                        />
                                    </FormField>
                                )}
                            />

                            <div className="text-lg font-semibold mt-2">
                                {t('netsuite.form.header.integrationUniversal')}
                            </div>

                            <Controller
                                name="universalFileCabinetFolderId"
                                control={form.control}
                                render={({ field }) => (
                                    <FormField
                                        label={t('netsuite.form.universalFileCabinetFolderId')}
                                        error={formErrors.universalFileCabinetFolderId}
                                        helpText={t('netsuite.form.universalFileCabinetFolderIdHelpText')}
                                    >
                                        <StringField {...field} />
                                    </FormField>
                                )}
                            />

                            <Controller
                                name="universalChannelIds"
                                control={form.control}
                                render={({ field }) => (
                                    <FormField
                                        label={t('netsuite.form.universalChannelIds')}
                                        error={formErrors.universalChannelIds}
                                    >
                                        <CheckboxGroup
                                            {...field}
                                            options={universalChannels.map((c) => ({ value: c.id, label: c.name }))}
                                            onValueChange={(value) => field.onChange({ target: { value } })}
                                        />
                                    </FormField>
                                )}
                            />
                        </>
                    )}

                    {isInvoiceMode && (
                        <>
                            <div className="text-lg font-semibold mt-2">
                                {t('netsuite.form.header.integrationInvoices')}
                            </div>

                            <Controller
                                name="invoiceFileCabinetFolderId"
                                control={form.control}
                                render={({ field }) => (
                                    <FormField
                                        label={t('netsuite.form.invoiceFileCabinetFolderId')}
                                        error={formErrors.invoiceFileCabinetFolderId}
                                        helpText={t('netsuite.form.invoiceFileCabinetFolderIdHelpText')}
                                    >
                                        <StringField {...field} />
                                    </FormField>
                                )}
                            />

                            <Controller
                                name="invoiceCustomFormId"
                                control={form.control}
                                render={({ field }) => (
                                    <FormField
                                        label={t('netsuite.form.invoiceCustomFormId')}
                                        error={formErrors.invoiceCustomFormId}
                                        helpText={t('netsuite.form.invoiceCustomFormIdHelpText')}
                                    >
                                        <StringField {...field} />
                                    </FormField>
                                )}
                            />

                            <Controller
                                name="invoiceChannelIds"
                                control={form.control}
                                render={({ field }) => (
                                    <FormField
                                        label={t('netsuite.form.invoiceChannelIds')}
                                        error={formErrors.invoiceChannelIds}
                                    >
                                        <CheckboxGroup
                                            {...field}
                                            options={invoiceChannels.map((c) => ({ value: c.id, label: c.name }))}
                                            onValueChange={(value) => field.onChange({ target: { value } })}
                                        />
                                    </FormField>
                                )}
                            />

                            <Controller
                                name="defaultApprovalStatus"
                                control={form.control}
                                render={({ field }) => (
                                    <FormField
                                        label={t('netsuite.form.defaultApprovalStatus')}
                                        error={formErrors.defaultApprovalStatus}
                                        helpText={t('netsuite.form.defaultApprovalStatusHelpText')}
                                    >
                                        <StringField {...field} />
                                    </FormField>
                                )}
                            />

                            <Controller
                                name="defaultExpenseAccountId"
                                control={form.control}
                                render={({ field }) => (
                                    <FormField
                                        label={t('netsuite.form.defaultExpenseAccountId')}
                                        error={formErrors.defaultExpenseAccountId}
                                        helpText={t('netsuite.form.defaultExpenseAccountIdHelpText')}
                                    >
                                        <StringField {...field} />
                                    </FormField>
                                )}
                            />

                            <Controller
                                name="defaultSubsidiaryId"
                                control={form.control}
                                render={({ field }) => (
                                    <FormField
                                        label={t('netsuite.form.defaultSubsidiaryId')}
                                        error={formErrors.defaultSubsidiaryId}
                                        helpText={t('netsuite.form.defaultSubsidiaryIdHelpText')}
                                    >
                                        <StringField {...field} />
                                    </FormField>
                                )}
                            />

                            <Controller
                                name="defaultLocationId"
                                control={form.control}
                                render={({ field }) => (
                                    <FormField
                                        label={t('netsuite.form.defaultLocationId')}
                                        error={formErrors.defaultLocationId}
                                        helpText={t('netsuite.form.defaultLocationIdHelpText')}
                                    >
                                        <StringField {...field} />
                                    </FormField>
                                )}
                            />
                        </>
                    )}

                    <div className="text-lg font-semibold mt-2">{t('netsuite.form.header.altavia')}</div>

                    <Controller
                        name="useAltaviaMapping"
                        control={form.control}
                        render={({ field }) => (
                            <FormField
                                label={t('netsuite.form.useAltaviaMapping')}
                                error={formErrors.useAltaviaMapping}
                                helpText={t('netsuite.form.useAltaviaMappingHelpText')}
                            >
                                <BooleanField value={field.value} onValueChange={field.onChange} />
                            </FormField>
                        )}
                    />

                    <Controller
                        name="altaviaRestletUrl"
                        control={form.control}
                        render={({ field }) => (
                            <FormField
                                label={t('netsuite.form.altaviaRestletUrl')}
                                error={formErrors.altaviaRestletUrl}
                                helpText={t('netsuite.form.altaviaRestletUrlHelpText')}
                            >
                                <StringField {...field} />
                            </FormField>
                        )}
                    />
                </form>
            )}

            {isInvoiceMode && activeTab === 'invoiceMapper' && (
                <OutputMapperEditorPanel
                    outputMapperKey={NETSUITE_OUTPUT_MAPPER_KEY}
                    ownerId={integration?.id}
                    recordType="invoice"
                />
            )}
        </div>
    );
};

export default NetsuiteIntegrationForm;
